/**
 * Edge case handling for country rendering
 * Phase 4.2: Handle date line wrapping and pole coverage
 *
 * Ported from Python (map_projection/svg_model.py)
 *
 * NOTE: JavaScript uses normalized [0,1] coordinates while Python uses absolute pixel coordinates
 * Thresholds are adapted accordingly (e.g., halfWidth=0.5 in JS vs width/2 pixels in Python)
 */

/**
 * Returns the number of times the path is 'broken'. In practice this means that:
 *   0 - path is intact, can be rendered directly.
 *   1 - only split by pole.
 *   even - path is split by ±180 longitude.
 *   odd - path is split by pole and ±180 longitude.
 *
 * Python equivalent: path_covers_pole(path, half_width)
 *
 * @param {Float32Array} coords2D - Normalized [x,y] coords in [0,1]
 * @param {number} halfWidth - Threshold for detecting jumps (typically 0.5 for normalized coords)
 * @returns {number} Number of breaks/jumps in the path
 */
export function pathCoversPole(coords2D, halfWidth = 0.5) {
    let jumpCount = 0;

    // Iterate through consecutive point pairs
    for (let i = 2; i < coords2D.length; i += 2) {
        const prevX = coords2D[i - 2];
        const prevY = coords2D[i - 1];  // Fixed: was i-3, should be i-1
        const currX = coords2D[i];
        const currY = coords2D[i + 1];

        const deltaX = Math.abs(currX - prevX);
        const deltaY = Math.abs(currY - prevY);

        // Count EACH coordinate (x and y separately) that exceeds threshold
        // Matches Python: np.count_nonzero(delta > half_width) counts all elements
        if (deltaX > halfWidth) {
            jumpCount++;
        }
        if (deltaY > halfWidth) {
            jumpCount++;
        }
    }

    return jumpCount;
}

/**
 * Add pole points to path when it crosses a pole
 * Inserts corner points at (0, pole) and (1, pole) when detecting longitude jumps
 *
 * Python equivalent: add_pole_points_to_path(path, half_width, width, half_height, height)
 *
 * @param {Float32Array} coords2D - Normalized [x,y] in [0,1]
 * @param {number} halfWidth - Threshold for detecting x-jumps (typically 0.5)
 * @param {number} width - Full width (1.0 for normalized)
 * @param {number} halfHeight - Half height threshold (typically 0.5)
 * @param {number} height - Full height (1.0 for normalized)
 * @returns {Float32Array} Enhanced path with pole points inserted
 */
export function addPolePointsToPath(coords2D, halfWidth = 0.5, width = 1.0, halfHeight = 0.5, height = 1.0) {
    const resultingPath = [];

    if (coords2D.length < 2) {
        return coords2D;
    }

    let prevX = coords2D[0];

    // Iterate through all points
    for (let i = 0; i < coords2D.length; i += 2) {
        const x = coords2D[i];
        const y = coords2D[i + 1];

        // Check for forward jump (x increases by more than halfWidth)
        if (x - prevX > halfWidth) {
            // Determine which pole based on y position
            const pole = y < halfHeight ? 0 : height;
            // Add pole points: (0, pole) then (width, pole)
            resultingPath.push(0, pole);
            resultingPath.push(width, pole);
        }
        // Check for backward jump (x decreases by more than halfWidth)
        else if (x - prevX < -halfWidth) {
            // Determine which pole based on y position
            const pole = y < halfHeight ? 0 : height;
            // Add pole points: (width, pole) then (0, pole)
            resultingPath.push(width, pole);
            resultingPath.push(0, pole);
        }

        // Add the current point
        resultingPath.push(x, y);
        prevX = x;
    }

    return new Float32Array(resultingPath);
}

/**
 * Helper function to process path until first jump is encountered
 * Used by duplicate() to determine initial split direction
 *
 * Python equivalent: _till_first_jump(path, width, middle_x)
 *
 * @param {Float32Array} coords2D - Normalized [x,y] coordinates
 * @param {number} width - Full width (1.0 for normalized)
 * @param {number} middleX - Jump threshold (typically 0.5)
 * @returns {Object} { initialLeft: boolean, firstJump: number, leftPart: Float32Array, rightPart: Float32Array }
 */
function _tillFirstJump(coords2D, width, middleX) {
    // Find first jump
    let firstJump = -1;
    for (let i = 2; i < coords2D.length; i += 2) {
        const prevX = coords2D[i - 2];
        const currX = coords2D[i];
        const deltaX = Math.abs(currX - prevX);

        if (deltaX > middleX) {
            firstJump = i;
            break;
        }
    }

    if (firstJump === -1) {
        throw new Error('Path never jumped');
    }

    // Extract coordinates up to (but not including) the jump
    const notShifted = new Float32Array(coords2D.slice(0, firstJump));

    // Determine initial left flag based on jump direction
    const prevX = coords2D[firstJump - 2];
    const currX = coords2D[firstJump];

    if (currX > prevX) {
        // Jump upward: left=original, right=shifted right
        const rightShifted = new Float32Array(firstJump);
        for (let i = 0; i < firstJump; i += 2) {
            rightShifted[i] = notShifted[i] + width;
            rightShifted[i + 1] = notShifted[i + 1];
        }
        return {
            initialLeft: true,
            firstJump: firstJump / 2,  // Convert to vertex index
            leftPart: notShifted,
            rightPart: rightShifted
        };
    } else {
        // Jump downward: left=shifted left, right=original
        const leftShifted = new Float32Array(firstJump);
        for (let i = 0; i < firstJump; i += 2) {
            leftShifted[i] = notShifted[i] - width;
            leftShifted[i + 1] = notShifted[i + 1];
        }
        return {
            initialLeft: false,
            firstJump: firstJump / 2,  // Convert to vertex index
            leftPart: leftShifted,
            rightPart: notShifted
        };
    }
}

/**
 * Duplicate a path that wraps around ±180° longitude
 * Creates two versions: one for left side, one for right side
 *
 * Python equivalent: duplicate(path, width, middle_x)
 *
 * @param {Float32Array} coords2D - Normalized [x,y] in [0,1]
 * @param {number} width - Full width (1.0 for normalized)
 * @param {number} middleX - Jump threshold (typically 0.5)
 * @returns {Array<Float32Array>} [leftPath, rightPath]
 */
export function duplicate(coords2D, width = 1.0, middleX = 0.5) {
    const numVertices = coords2D.length / 2;
    const leftPath = new Float32Array(coords2D.length);
    const rightPath = new Float32Array(coords2D.length);

    // Process portion up to first jump to determine initial direction
    const { initialLeft } = _tillFirstJump(coords2D, width, middleX);

    // Build toggle array for remaining points
    const jumps = new Uint8Array(numVertices);
    jumps[0] = 0;

    for (let i = 1; i < numVertices; i++) {
        const prevX = coords2D[(i - 1) * 2];
        const currX = coords2D[i * 2];
        const deltaX = Math.abs(currX - prevX);

        if (deltaX > middleX) {
            jumps[i] = jumps[i - 1] + 1;
        } else {
            jumps[i] = jumps[i - 1];
        }
    }

    // Apply toggles to determine left/right for each vertex
    for (let i = 0; i < numVertices; i++) {
        const x = coords2D[i * 2];
        const y = coords2D[i * 2 + 1];
        const toggleCount = jumps[i];

        // Determine if this point should be on left (original) or shifted
        const isLeft = (toggleCount % 2 === 0) ? initialLeft : !initialLeft;

        if (isLeft) {
            // Left gets original, right gets shifted right
            leftPath[i * 2] = x;
            leftPath[i * 2 + 1] = y;
            rightPath[i * 2] = x + width;
            rightPath[i * 2 + 1] = y;
        } else {
            // Left gets shifted left, right gets original
            leftPath[i * 2] = x - width;
            leftPath[i * 2 + 1] = y;
            rightPath[i * 2] = x;
            rightPath[i * 2 + 1] = y;
        }
    }

    return [leftPath, rightPath];
}

/**
 * Process a single path with all edge case handling
 * Matches Python logic from SVGModel.projected_paths()
 *
 * @param {Float32Array} coords2D - Normalized [x,y] coordinates in [0,1]
 * @param {number} halfWidth - Jump threshold (typically 0.5 for normalized)
 * @param {number} width - Full width (1.0 for normalized)
 * @param {number} halfHeight - Half height (0.5 for normalized)
 * @param {number} height - Full height (1.0 for normalized)
 * @returns {Array<Float32Array>} Array of processed coordinate arrays (1 or 2 paths)
 */
export function processPathEdgeCases(coords2D, halfWidth = 0.5, width = 1.0, halfHeight = 0.5, height = 1.0) {
    const pathCheck = pathCoversPole(coords2D, halfWidth);

    if (pathCheck === 0) {
        // Path is intact, render directly
        return [coords2D];
    } else if (pathCheck % 2 === 1) {
        // Path is split by pole (odd number)
        const enhanced = addPolePointsToPath(coords2D, halfWidth, width, halfHeight, height);
        return [enhanced];
    } else {
        // Path is split by ±180° longitude (even number)
        const [leftPath, rightPath] = duplicate(coords2D, width, halfWidth);
        return [leftPath, rightPath];
    }
}
